import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { updatePassword } from "../../redux/features/authSlice"; // Adjust the import path as necessary
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";

const ResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error, success } = useSelector((state) => state.auth);

  // Show toast notification on successful password reset
  useEffect(() => {
    if (success) {
      toast.success("Password reset successful!", {
        position: "top-right",
        autoClose: 5000, // Toast will disappear after 5 seconds
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [success]);

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .required("New password is required")
        .min(9, "Password must be at least 9 characters")
        .max(64, "Password must be less than 64 characters")
        .matches(/[A-Z]/, "Password must include at least one uppercase letter")
        .matches(/[a-z]/, "Password must include at least one lowercase letter")
        .matches(/[0-9]/, "Password must include at least one number")
        .matches(
          /[\W_]/,
          "Password must include at least one special character"
        ),
      confirmPassword: Yup.string()
        .required("Confirm your password")
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
    }),
    onSubmit: (values) => {
      if (location.state?.email) {
        dispatch(
          updatePassword({
            email: location.state?.email,
            newPassword: values.newPassword,
          })
        )
          .unwrap()
          .then((res) => {
            toast.success("Password reset successful!", {
              position: "top-right",
              autoClose: 3000, // Toast will disappear after 5 seconds
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
            navigate("/login");
          })
          .catch((err) => {
            console.log("Error", err);
          });
      } else {
        toast.error("Please try again later", {
          position: "center",
          autoClose: 3000,
        });
      }
    },
  });

  return (
    <div>
      <ToastContainer />{" "}
      {/* This is where the toast notifications will appear */}
      <main className="main pages">
        <div className="page-header breadcrumb-wrap">
          <div className="container">
            <div className="breadcrumb">
              <a rel="nofollow">
                <i className="fi-rs-home mr-5" />
                Home
              </a>
              <span /> Pages <span /> My Account
            </div>
          </div>
        </div>
        <div className="page-content pt-50 pb-150">
          <div className="container">
            <div className="row">
              <div className="col-xl-6 col-lg-8 col-md-12 m-auto">
                <div className="row">
                  <div className="heading_s1">
                    <img
                      className="border-radius-15"
                      src="././assets/imgs/page/reset_password.svg"
                      alt=""
                    />
                    <h2 className="mb-15 mt-15">Set new password</h2>
                    <p className="mb-30">
                      Please create a new password that you don’t use on any
                      other site.
                    </p>
                  </div>
                  <div className="col-lg-6 col-md-8">
                    <div className="login_wrap widget-taber-content background-white">
                      <div className="padding_eight_all bg-white">
                        <form onSubmit={formik.handleSubmit}>
                          <div className="form-group mb-4">
                            <input
                              type="password"
                              name="newPassword"
                              placeholder="New Password *"
                              {...formik.getFieldProps("newPassword")}
                              className={`form-control ${
                                formik.touched.newPassword &&
                                formik.errors.newPassword
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            {formik.touched.newPassword &&
                            formik.errors.newPassword ? (
                              <div className="text-danger text-sm mt-1">
                                {formik.errors.newPassword}
                              </div>
                            ) : null}
                          </div>
                          <div className="form-group mb-4">
                            <input
                              type="password"
                              name="confirmPassword"
                              placeholder="Confirm New Password *"
                              {...formik.getFieldProps("confirmPassword")}
                              className={`form-control ${
                                formik.touched.confirmPassword &&
                                formik.errors.confirmPassword
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            {formik.touched.confirmPassword &&
                            formik.errors.confirmPassword ? (
                              <div className="text-danger text-sm mt-1">
                                {formik.errors.confirmPassword}
                              </div>
                            ) : null}
                          </div>
                          <div className="form-group mb-4">
                            <button
                              type="submit"
                              className="btn btn-fill-out btn-block hover-up font-weight-bold"
                              disabled={isLoading}
                            >
                              {isLoading ? "Resetting..." : "Reset Password"}
                            </button>
                          </div>
                          {error && (
                            <div className="text-danger text-sm mt-1">
                              {error}
                            </div>
                          )}
                        </form>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6 pl-10">
                    <h6 className="mb-4">Password must:</h6>
                    <p className="mb-2">Be between 9 and 64 characters</p>
                    <p className="mb-2">
                      Include at least two of the following:
                    </p>
                    <ol className="list-disc list-inside ml-4">
                      <li>An uppercase character</li>
                      <li>A lowercase character</li>
                      <li>A number</li>
                      <li>A special character</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
